import isPermissionEnabled from "common/isPermissionEnabled"
import Breadcrumbs from "components/Common/Breadcrumb"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import TableShimmer from "components/Shared/TableShimmer"
import formatDateTime from "components/Shared/formatDateTime"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Card, CardBody, Col, Row, Table } from "reactstrap"
import { getAllCouponUsage } from "store/actions/promotionsAction"
import { getAllBranchs } from "store/actions/usersAction"
import { getCrossModuleFilterPermissions } from "utils/crossModuleFilterPermissions"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"

const CouponUsage = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Coupon Usage ")

  const dispatch = useDispatch()
  const location = useLocation()
  const { couponUsageId } = useParams()

  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const branchIdParam = searchParams.get("branchId")
  const minDateParam = searchParams.get("minDate")
  const maxDateParam = searchParams.get("maxDate")
  const navigate = useNavigate()
  const flatpickrRef = useRef(null)

  const {
    loading,
    couponsItemsPerPage: itemsPerPage,
    totalCount,
    totalPages,
    currentPage,
    isUpdating,
    isLoading,
    couponUsage,
  } = useSelector(state => state.Promotions)

  const { userRole, accountType, accountScope } = useSelector(
    state => state.Login
  )
  const { canFilterByBranch } = getCrossModuleFilterPermissions(
    userRole,
    accountType,
    accountScope
  )
  const { branchsList } = useSelector(state => state.Users)

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      14,
      "promotion_1",
      accountType,
      accountScope
    )

    if (!isPermissionList) {
      navigateToAccessibleRoute(navigate, { userRole, accountType })
    }
    setUsersPermissions({
      list: isPermissionList,
    })
  }, [userRole, accountType, accountScope, navigate])

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [dateRange, setDateRange] = useState(() =>
    minDateParam && maxDateParam
      ? { min: minDateParam, max: maxDateParam }
      : {}
  )

  const [selectedFilters, setSelectedFilters] = useState({
    sortOrder: parseInt(searchParams.get("sortOrder") || "2"),
    coupon: "",
    branchId: branchIdParam
      ? branchIdParam.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
  })

  useEffect(() => {
    if (canFilterByBranch) {
      dispatch(getAllBranchs())
    }
  }, [canFilterByBranch, dispatch])

  useEffect(() => {
    fetchCouponUsage()
  }, [mode])

  const buildPayload = (page = pageNumber) => {
    const { sortOrder, branchId } = selectedFilters
    const branchIds = canFilterByBranch
      ? branchId?.map(item => item.value) || []
      : []
    const hasDateRange = dateRange?.min && dateRange?.max

    return {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      coupon: mode === "couponUsage" ? couponUsageId : "",
      branchId: branchIds,
      dateRange: hasDateRange ? { min: dateRange.min, max: dateRange.max } : {},
    }
  }

  const fetchCouponUsage = () => {
    dispatch(getAllCouponUsage(buildPayload()))
    setIsApiCalled(true)
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const filters = [
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
    ...(canFilterByBranch
      ? [
          {
            name: "branchId",
            type: "multiSelect",
            options: (branchsList || []).map(branch => ({
              label: language === "sa" ? branch.nameAr : branch.name,
              value: branch._id,
            })),
            placeholder: t("Select Branch"),
          },
        ]
      : []),
  ]

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const getApiCall = (page = 1) => {
    const { sortOrder, branchId } = selectedFilters
    const hasDateRange = dateRange?.min && dateRange?.max

    const updatedSearchParams = new URLSearchParams()
    updatedSearchParams.set("search", searchTerm)
    updatedSearchParams.set("sortOrder", sortOrder.toString())
    updatedSearchParams.set("page", page.toString())

    if (branchId?.length > 0) {
      const branchIdString = branchId
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      updatedSearchParams.set("branchId", branchIdString)
    }

    if (hasDateRange) {
      updatedSearchParams.set("minDate", dateRange.min?.toString())
      updatedSearchParams.set("maxDate", dateRange.max?.toString())
    }

    return dispatch(getAllCouponUsage(buildPayload(page)))
      .then(action => {
        const { statusCode, status } = action?.payload
        if (statusCode === 200 && status) {
          const path =
            mode === "couponUsage"
              ? `/coupon-usage/${couponUsageId}`
              : `/coupon-usage`
          navigate(`${path}?${updatedSearchParams.toString()}`)
        }
      })
      .catch(error => {
        console.error(error)
      })
  }

  const handleSearch = () => {
    getApiCall() // Default page = 1
  }

  const onPageChange = page => {
    getApiCall(page)
  }

  const handleReset = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      orderBy: 2,
      keyword: "",
      coupon: mode === "couponUsage" ? couponUsageId : "",
      branchId: [],
      dateRange: {},
    }
    dispatch(getAllCouponUsage(payload))
    setSelectedFilters({
      sortOrder: 2,
      coupon: "",
      branchId: [],
    })
    setSearchTerm("")
    setDateRange({})
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.clear()
    }
    mode === "couponUsage"
      ? navigate(`/coupon-usage/${couponUsageId}?page=${1}`)
      : navigate(`/coupon-usage?page=${1}`)
  }

  const buildExportPayload = exportType => {
    const { sortOrder, branchId } = selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const hasDateRange = dateRange?.min && dateRange?.max

    return {
      status: 1,
      keyword: searchTerm || "",
      type: "COUPON-USAGE",
      exportType,
      orderBy: Number(sortOrder),
      coupon: mode === "couponUsage" ? couponUsageId : "",
      branchId: branchIds,
      dateRange: hasDateRange ? { min: dateRange.min, max: dateRange.max } : {},
    }
  }

  const handleExcelDownload = async () => {
    const response = await pdfDownload(EXPORT_DATA, buildExportPayload("xlsx"))
    if (response) {
      const xlsxData = response
      DownloadXLSX(xlsxData, "Coupon Usage")
    }
  }

  const exportToCSV = async () => {
    const response = await pdfDownload(EXPORT_DATA, buildExportPayload("csv"))
    if (response) {
      const csvData = response
      DownloadCSV(csvData, "Coupon Usage")
    }
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        {mode !== "couponUsage" && <Breadcrumbs />}
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Coupon Usage")}
                isExportData={loading ? false : true}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
              />
              <CardBody>
                {/* {!loading && ( */}
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Coupon Code / Coupon Name / User Name")}
                  isRangeDate={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                />

                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={7} />
                  ) : couponUsage.length === 0 ? (
                    <p className="text-center my-2">
                      {t("No coupons usage found")}
                    </p>
                  ) : (
                    <Table className="table mb-0 capitalize_td" hover>
                      <thead className="table-light">
                        <tr>
                          <th>{t("S. No.")}</th>
                          <th>{t("Coupon Code")}</th>
                          <th>{t("Coupon Name")}</th>
                          <th>{t("User")}</th>
                          <th>{t("Branch Name")}</th>
                          <th>{t("Order Date")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {couponUsage?.map((item, index) => (
                          <tr
                            key={item._id}
                            onClick={() =>
                              navigate(`/coupon-usage/details?id=${item._id}`)
                            }
                            className="cursor-pointer"
                          >
                            <th scope="row">
                              {(currentPage - 1) * 10 + index + 1}
                            </th>
                            <td>{item?.coupon?.code}</td>
                            <td>
                              {language === "sa"
                                ? item?.coupon?.nameAr
                                : item?.coupon?.name}
                            </td>
                            <td>{`${item?.userId?.firstName} ${item?.userId?.lastName}`}</td>
                            <td>
                              {language === "sa"
                                ? item?.branchId?.nameAr
                                : item?.branchId?.name}
                            </td>
                            <td>{formatDateTime(item?.order?.orderDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && (
                  <div className="mt-3">
                    <Paginations
                      data={couponUsage}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default CouponUsage
